import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Capacitor } from '@capacitor/core';
import { App } from '@capacitor/app';

/**
 * DeepLinkListener — routes URLs the OS hands to the native shell.
 *
 * Week 4 store build (Sept 2026), B-14 phase 2 + the membership-card shortcut:
 *   - Android App Links / iOS Universal Links: an invite link
 *     (https://<APP_URL>/:clubSlug/invite/:token) opens the app instead of the
 *     browser and lands on the same InviteAcceptPage the web uses.
 *   - Home-screen shortcut / quick action: https://<APP_URL>/wallet → the
 *     /wallet route picks the right club and opens the Wallet on the card.
 *
 * Only the PATH is taken from the URL: whatever host the OS matched, the app
 * navigates inside itself, so a link can never make the WebView leave the
 * bundle. Web renders nothing and registers nothing.
 *
 * Cold start: on some Android versions the `appUrlOpen` event fires before
 * the bridge is listening, so the launch URL is also read once via
 * getLaunchUrl(). That value never changes for the life of the process, so it
 * is consumed ONCE per JS session (module flag — survives StrictMode's double
 * effect and any remount); the listener is registered once (effect deps [],
 * navigate held in a ref — useNavigate returns a new function on every route
 * change, and keying the effect on it would re-read the launch URL after each
 * navigation and yank the user back — marakin-qa Week 4 M1).
 */
let launchUrlConsumed = false;

export default function DeepLinkListener() {
  const navigate = useNavigate();
  const navigateRef = useRef(navigate);
  navigateRef.current = navigate;
  const lastRef = useRef({ url: null, at: 0 });

  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return undefined;

    const open = (raw, { replace = false } = {}) => {
      if (!raw) return;
      const now = Date.now();
      // appUrlOpen and getLaunchUrl can both report the cold-start URL.
      if (lastRef.current.url === raw && now - lastRef.current.at < 5000) return;
      lastRef.current = { url: raw, at: now };
      let target;
      try {
        const u = new URL(raw);
        if (!/^https?:$/.test(u.protocol)) return;
        target = `${u.pathname}${u.search}${u.hash}`;
      } catch {
        return;
      }
      if (!target.startsWith('/')) return;
      navigateRef.current(target, { replace });
    };

    const sub = App.addListener('appUrlOpen', ({ url }) => open(url));
    if (!launchUrlConsumed) {
      launchUrlConsumed = true;
      // replace: the launch URL should not leave "/" underneath it in history.
      App.getLaunchUrl().then((r) => open(r?.url, { replace: true })).catch(() => {});
    }

    return () => { sub.then((h) => h.remove()).catch(() => {}); };
  }, []);

  return null;
}
